import { DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';
import { EnvManagerApi } from './api.interface';
import { CreateEnvironmentVariable, UpdateEnvironmentVariable } from './dto/request/environmentvariable';
import { EnvironmentVariableRoot, EnvironmentVariableSingleObject } from './dto/response/environmentvariable';
import CustomError from '../../components/shared/error-handling/error';
import { ScmAuthApi } from '@backstage/integration-react';
import { ConfigFile } from './dto/response/configfile';
import { CreateConfigFile, UpdateConfigFile } from './dto/request/configfile';

export class EnvManagerApiClient implements EnvManagerApi {

    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
    scmAuthApi: ScmAuthApi;


    constructor({ discoveryApi, identityApi, scmAuthApi }: { discoveryApi: DiscoveryApi, identityApi: IdentityApi, scmAuthApi: ScmAuthApi }) {
        this.discoveryApi = discoveryApi;
        this.identityApi = identityApi;
        this.scmAuthApi = scmAuthApi;
    }

    private async fetch<T = any>(input: string, init?: RequestInit): Promise<T> {
        // As configured previously for the backend proxy
        const proxyUri = `${await this.discoveryApi.getBaseUrl('proxy')}/envmanager`;
        const gitlabUrl = await this.discoveryApi.getBaseUrl('gitlab');

        const token = (await this.identityApi.getCredentials()).token;
        let userCredentials = (await this.scmAuthApi.getCredentials({ instantPopup: false,  optional: true, url: gitlabUrl, additionalScope: { customScopes: { gitlab: ['api'] } } }));
        if (!userCredentials.token) 
            userCredentials = (await this.scmAuthApi.getCredentials({ instantPopup: false, url: gitlabUrl, additionalScope: { customScopes: { gitlab: ['api'] } } }));

        const headers = {
            ...init?.headers,
            'Authorization': `Bearer ${token}`,
            'user-authorization': `Bearer ${userCredentials.token}`,
            'Content-Type': 'application/json'
        };

        // Create a new init object with merged headers
        const newInit: RequestInit = {
            ...init,
            headers: headers
        };

        const resp = await fetch(`${proxyUri}${input}`, newInit);
        if (!resp.ok) {
            throw new CustomError((await resp.json()).message, resp.status);
        }
        if (resp.status !== 204) {
            return await resp.json();
        }
        return Promise.resolve() as Promise<T>;
    }

    async listEnvironmentVariables(environment: string, system_name: string, application_name: string): Promise<EnvironmentVariableRoot> {
        return await this.fetch<EnvironmentVariableRoot>(`/environment/${environment}/system/${system_name}/application/${application_name}/environment-variable`);
    }

    async createEnvironmentVariable(environment: string, system_name: string, application_name: string, environment_variable: CreateEnvironmentVariable): Promise<EnvironmentVariableSingleObject> {
        return await this.fetch<EnvironmentVariableSingleObject>(`/environment/${environment}/system/${system_name}/application/${application_name}/environment-variable`, {
            method: 'POST',
            body: JSON.stringify(environment_variable)
        });
    }

    async updateEnvironmentVariable(environment: string, system_name: string, application_name: string, key: string, environment_variable: UpdateEnvironmentVariable): Promise<EnvironmentVariableSingleObject> {
        return await this.fetch<EnvironmentVariableSingleObject>(`/environment/${environment}/system/${system_name}/application/${application_name}/environment-variable/${key}`, {
            method: 'PUT',
            body: JSON.stringify(environment_variable)
        });
    }

    async deleteEnvironmentVariable(environment: string, system_name: string, application_name: string, key: string): Promise<boolean> {
        return await this.fetch<boolean>(`/environment/${environment}/system/${system_name}/application/${application_name}/environment-variable/${key}`,
            { method: 'DELETE' }
        );
    }

    async getConfigFile(environment: string, system_name: string, application_name: string): Promise<ConfigFile> {
        return await this.fetch<ConfigFile>(`/environment/${environment}/system/${system_name}/application/${application_name}/config-file`);
    }

    async createConfigFile(environment: string, system_name: string, application_name: string, config_file: CreateConfigFile): Promise<ConfigFile> {
        return await this.fetch<ConfigFile>(`/environment/${environment}/system/${system_name}/application/${application_name}/config-file`, {
            method: 'POST',
            body: JSON.stringify(config_file)
        });
    }

    async updateConfigFile(environment: string, system_name: string, application_name: string, config_file: UpdateConfigFile): Promise<ConfigFile> {
        return await this.fetch<ConfigFile>(`/environment/${environment}/system/${system_name}/application/${application_name}/config-file`, {
            method: 'PUT',
            body: JSON.stringify(config_file)
        });
    }

    async deleteConfigFile(environment: string, system_name: string, application_name: string): Promise<boolean> {
        return await this.fetch<boolean>(`/environment/${environment}/system/${system_name}/application/${application_name}/config-file`,
            { method: 'DELETE' }
        );
    }
}